'use client'

import { Point } from '../../../icons'
import { cn } from '../../../utils/cn'
import { formatNumber } from '../../../utils/formatNumber'
import { FeeBreakdown } from '../FeeBreakdown'

export interface PointCardProps {
  /**
   * Class name
   */
  className?: string

  /**
   * Optional heading rendered above the row (e.g. 不足ポイントのみ購入)
   */
  title?: string

  /**
   * Points granted, which equals the price in yen
   */
  value: number

  /**
   * Small payment handling fee in yen. Above 0 the plain price is replaced by
   * a shortage + fee breakdown.
   */
  transactionFee?: number

  /**
   * Whether the card is selected
   */
  selected?: boolean

  onClick?: () => void
}

/**
 * Simple point pack card used for reservation / corporate flows.
 * Unlike `PlanCard` there is no check icon — selection is expressed by the
 * border and background alone.
 */
export const PointCard = ({
  className,
  title,
  value,
  transactionFee = 0,
  selected,
  onClick,
}: PointCardProps) => {
  const showFeeBreakdown = transactionFee > 0

  return (
    <button
      type="button"
      className={cn(
        'flex w-full flex-col items-start gap-2 rounded-md border border-solid p-4 transition-all',
        selected ? 'border-red-01 bg-red-04' : 'border-gray-04 bg-white',
        className,
      )}
      aria-pressed={!!selected}
      onClick={onClick}
    >
      {title && <div className="text-gray-01 typography-subhead2">{title}</div>}

      <div className="flex w-full items-center justify-between">
        <div className="flex flex-1 items-center gap-2">
          <span className="flex size-6 items-center justify-center rounded-full bg-red-01">
            <Point className="size-4 text-white" />
          </span>

          <span className="text-red-01 typography-subhead1">
            {formatNumber(value)}pt
          </span>
        </div>

        {!showFeeBreakdown && (
          <span className="text-blue-01 typography-subhead1">
            {formatNumber(value)}円
          </span>
        )}
      </div>

      {showFeeBreakdown && (
        <FeeBreakdown
          className="mx-auto"
          value={value}
          transactionFee={transactionFee}
        />
      )}
    </button>
  )
}
